/**
 * Walkeaze Theme JavaScript
 * Shopify Online Store 2.0 - Production Theme
 */

(function () {
  'use strict';

  /* ================================================
     UTILITY FUNCTIONS
  ================================================ */
  const $ = (selector, context = document) => context.querySelector(selector);
  const $$ = (selector, context = document) => [...context.querySelectorAll(selector)];

  function debounce(fn, delay = 200) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  function getScrollbarWidth() {
    return window.innerWidth - document.documentElement.clientWidth;
  }

  function lockScroll() {
    const sb = getScrollbarWidth();
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = sb + 'px';
  }

  function unlockScroll() {
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  }

  /* ================================================
     1. ANNOUNCEMENT BAR
  ================================================ */
  function initAnnouncementBar() {
    const bar = $('#announcement-bar');
    const closeBtn = $('#ann-close');
    if (!bar) return;

    // Check if dismissed in this session
    if (sessionStorage.getItem('wkz-ann-dismissed')) {
      bar.style.display = 'none';
      document.documentElement.style.setProperty('--ann-bar-height', '0px');
      return;
    }

    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        bar.style.maxHeight = bar.offsetHeight + 'px';
        requestAnimationFrame(() => {
          bar.style.transition = 'max-height 0.4s ease, opacity 0.3s ease';
          bar.style.maxHeight = '0';
          bar.style.opacity = '0';
        });
        setTimeout(() => {
          bar.style.display = 'none';
          document.documentElement.style.setProperty('--ann-bar-height', '0px');
        }, 420);
        sessionStorage.setItem('wkz-ann-dismissed', '1');
      });
    }

    // Pause on hover
    const content = bar.querySelector('.announcement-bar__content');
    if (content) {
      bar.addEventListener('mouseenter', () => content.style.animationPlayState = 'paused');
      bar.addEventListener('mouseleave', () => content.style.animationPlayState = 'running');
    }
  }

  /* ================================================
     2. HEADER - Sticky + Transparent + Mobile Drawer
  ================================================ */
  function initHeader() {
    const header = $('#site-header');
    if (!header) return;

    const overlay = $('#drawer-overlay');
    const navToggle = $('#nav-toggle');
    const navClose = $('#nav-close');
    const mobileDrawer = $('#mobile-drawer');
    const searchToggle = $('#search-toggle');
    const searchClose = $('#search-close');
    const searchBar = $('#search-bar');

    let lastScrollY = 0;
    let ticking = false;

    // Sticky header with hide-on-scroll-down
    function onScroll() {
      const currentScrollY = window.scrollY;

      if (currentScrollY > 60) {
        header.classList.add('site-header--scrolled');
      } else {
        header.classList.remove('site-header--scrolled');
      }

      // If transparent on home, handle color change
      if (header.classList.contains('site-header--transparent')) {
        if (currentScrollY > 80) {
          header.classList.add('site-header--scrolled');
        }
      }

      lastScrollY = currentScrollY;
      ticking = false;
    }

    // Always attach scroll listener - header is fixed on all pages
    window.addEventListener('scroll', function () {
      if (!ticking) {
        requestAnimationFrame(onScroll);
        ticking = true;
      }
    }, { passive: true });

    // Mobile drawer
    function openDrawer() {
      if (!mobileDrawer) return;
      mobileDrawer.classList.add('is-open');
      mobileDrawer.setAttribute('aria-hidden', 'false');
      if (overlay) overlay.classList.add('is-visible');
      if (navToggle) navToggle.setAttribute('aria-expanded', 'true');
      header.classList.add('site-header--drawer-open');
      document.body.classList.add('drawer-open');
      lockScroll();
      // Focus first link
      setTimeout(() => {
        const firstLink = mobileDrawer.querySelector('a, button');
        if (firstLink) firstLink.focus();
        document.dispatchEvent(new Event('wkz:drawerOpen'));
      }, 400);
    }

    function closeDrawer() {
      if (!mobileDrawer) return;
      mobileDrawer.classList.remove('is-open');
      mobileDrawer.setAttribute('aria-hidden', 'true');
      if (overlay) overlay.classList.remove('is-visible');
      if (navToggle) {
        navToggle.setAttribute('aria-expanded', 'false');
        navToggle.focus();
      }
      header.classList.remove('site-header--drawer-open');
      document.body.classList.remove('drawer-open');
      unlockScroll();
      document.dispatchEvent(new Event('wkz:drawerClose'));
    }

    /* nav-toggle: inline header script already attached this handler (data-drw-inline).
       Only add here as a fallback if the inline script somehow didn't run. */
    if (navToggle && !navToggle.dataset.drwInline) {
      navToggle.addEventListener('click', function () {
        if (mobileDrawer && mobileDrawer.classList.contains('is-open')) {
          closeDrawer();
        } else {
          openDrawer();
        }
      });
    }
    if (navClose) navClose.addEventListener('click', closeDrawer);
    if (overlay) overlay.addEventListener('click', closeDrawer);

    /* Expose enhanced open/close so the inline handler can delegate here once loaded */
    window._wkzDrawerOpen  = openDrawer;
    window._wkzDrawerClose = closeDrawer;

    // Escape key closes drawer
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        if (mobileDrawer && mobileDrawer.classList.contains('is-open')) closeDrawer();
        if (searchBar && searchBar.classList.contains('is-open')) closeSearch();
      }
    });

    // Search toggle
    function openSearch() {
      if (!searchBar) return;
      searchBar.classList.add('is-open');
      searchBar.setAttribute('aria-hidden', 'false');
      if (searchToggle) searchToggle.setAttribute('aria-expanded', 'true');
      setTimeout(() => {
        const input = searchBar.querySelector('input[type="search"]');
        if (input) input.focus();
      }, 150);
    }

    function closeSearch() {
      if (!searchBar) return;
      searchBar.classList.remove('is-open');
      searchBar.setAttribute('aria-hidden', 'true');
      if (searchToggle) {
        searchToggle.setAttribute('aria-expanded', 'false');
        searchToggle.focus();
      }
    }

    if (searchToggle) searchToggle.addEventListener('click', openSearch);
    if (searchClose) searchClose.addEventListener('click', closeSearch);
  }

  /* ================================================
     3. HERO CAROUSEL
  ================================================ */
  function initHeroCarousels() {
    $$('[id^="hero-carousel-"]').forEach(function (carousel) {
      const slides = $$('.hero-slide', carousel);
      const dots = $$('.hero-carousel__dot', carousel);
      const prevBtn = carousel.querySelector('.hero-carousel__prev');
      const nextBtn = carousel.querySelector('.hero-carousel__next');
      let current = 0;
      let autoplayTimer = null;
      const autoplay = carousel.dataset.autoplay === 'true';
      const autoplaySpeed = parseInt(carousel.dataset.autoplaySpeed, 10) || 5000;

      if (slides.length < 2) return;

      function goTo(index) {
        slides[current].classList.remove('hero-slide--active');
        if (dots[current]) dots[current].classList.remove('hero-carousel__dot--active');
        if (dots[current]) dots[current].setAttribute('aria-selected', 'false');

        current = (index + slides.length) % slides.length;

        slides[current].classList.add('hero-slide--active');
        if (dots[current]) dots[current].classList.add('hero-carousel__dot--active');
        if (dots[current]) dots[current].setAttribute('aria-selected', 'true');
      }

      function startAutoplay() {
        if (!autoplay) return;
        stopAutoplay();
        autoplayTimer = setInterval(() => goTo(current + 1), autoplaySpeed);
      }

      function stopAutoplay() {
        if (autoplayTimer) {
          clearInterval(autoplayTimer);
          autoplayTimer = null;
        }
      }

      if (prevBtn) {
        prevBtn.addEventListener('click', function () {
          goTo(current - 1);
          stopAutoplay();
          startAutoplay();
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          goTo(current + 1);
          stopAutoplay();
          startAutoplay();
        });
      }

      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () {
          goTo(i);
          stopAutoplay();
          startAutoplay();
        });
      });

      // Touch/swipe
      let touchStartX = 0, touchStartY = 0, didSwipe = false;
      carousel.addEventListener('touchstart', function (e) {
        touchStartX = e.changedTouches[0].clientX;
        touchStartY = e.changedTouches[0].clientY;
        didSwipe = false;
      }, { passive: true });

      carousel.addEventListener('touchmove', function (e) {
        const dx = Math.abs(e.changedTouches[0].clientX - touchStartX);
        const dy = Math.abs(e.changedTouches[0].clientY - touchStartY);
        if (dx > 8 && dx > dy) didSwipe = true;
      }, { passive: true });

      carousel.addEventListener('touchend', function (e) {
        const diff = touchStartX - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 40) {
          didSwipe = true;
          goTo(diff > 0 ? current + 1 : current - 1);
          stopAutoplay();
          startAutoplay();
        }
      }, { passive: true });

      // A swipe must NOT trigger the slide's full-cover link (this was opening
      // random pages / home when you swiped the hero). Cancel the trailing click.
      carousel.addEventListener('click', function (e) {
        if (didSwipe) {
          e.preventDefault();
          e.stopPropagation();
          didSwipe = false;
        }
      }, true);

      // Pause on hover
      carousel.addEventListener('mouseenter', stopAutoplay);
      carousel.addEventListener('mouseleave', startAutoplay);

      startAutoplay();
    });
  }

  /* ================================================
     4. CATEGORY GRID - Scroll-Snap Dot Navigation
  ================================================ */
  function initCategoryGrid() {
    $$('[id^="cat-scroll-"]').forEach(function (container) {
      const section = container.closest('.category-grid');
      if (!section) return;

      const items = $$('.category-grid__snap-item', container);
      const dots = $$('[data-cat-dot]', document);

      if (items.length < 2 || dots.length === 0) return;

      // Dot click scrolls to item
      dots.forEach(function (dot) {
        dot.addEventListener('click', function () {
          const idx = parseInt(this.dataset.catDot, 10);
          if (items[idx]) {
            items[idx].scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        });
      });

      // IntersectionObserver to update active dot
      const observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            const idx = items.indexOf(entry.target);
            if (idx === -1) return;
            dots.forEach(function (dot, i) {
              dot.classList.toggle('category-grid__dot--active', i === idx);
              dot.setAttribute('aria-selected', String(i === idx));
            });
          }
        });
      }, {
        root: container,
        threshold: 0.5
      });

      items.forEach(function (item) { observer.observe(item); });
    });
  }

  /* ================================================
     5. FILTER SIDEBAR TOGGLE
  ================================================ */
  function initFilterSidebar() {
    const filterToggle = $('#filter-toggle');
    const filterSidebar = $('#filter-sidebar');
    const filterClose = $('#filter-close');

    if (!filterToggle || !filterSidebar) return;

    function openFilter() {
      filterSidebar.classList.add('is-open');
      filterSidebar.setAttribute('aria-hidden', 'false');
      filterToggle.setAttribute('aria-expanded', 'true');
      // On mobile, show overlay
      if (window.innerWidth < 900) {
        const overlay = $('#drawer-overlay');
        if (overlay) {
          overlay.classList.add('is-visible');
          overlay.addEventListener('click', closeFilter, { once: true });
        }
        lockScroll();
      }
    }

    function closeFilter() {
      filterSidebar.classList.remove('is-open');
      filterSidebar.setAttribute('aria-hidden', 'true');
      filterToggle.setAttribute('aria-expanded', 'false');
      const overlay = $('#drawer-overlay');
      if (overlay) overlay.classList.remove('is-visible');
      unlockScroll();
    }

    filterToggle.addEventListener('click', function () {
      const isOpen = filterSidebar.classList.contains('is-open');
      if (isOpen) closeFilter(); else openFilter();
    });

    if (filterClose) filterClose.addEventListener('click', closeFilter);

    // Filter group toggles (accordion)
    $$('.filter-group__toggle').forEach(function (toggle) {
      toggle.addEventListener('click', function () {
        const expanded = this.getAttribute('aria-expanded') === 'true';
        this.setAttribute('aria-expanded', String(!expanded));
      });
    });
  }

  /* ================================================
     6. PRODUCT GALLERY
  ================================================ */
  function initProductGallery() {
    const gallery = $('.product-gallery');
    if (!gallery) return;

    const thumbs = $$('.product-gallery__thumb', gallery);
    const slides = $$('.product-gallery__slide', gallery);
    const prevBtn = gallery.querySelector('.product-gallery__arrow--prev');
    const nextBtn = gallery.querySelector('.product-gallery__arrow--next');
    let current = 0;

    function goTo(index) {
      if (!slides[index]) return;
      slides[current].classList.remove('product-gallery__slide--active');
      if (thumbs[current]) thumbs[current].classList.remove('product-gallery__thumb--active');
      if (thumbs[current]) thumbs[current].setAttribute('aria-selected', 'false');

      current = index;

      slides[current].classList.add('product-gallery__slide--active');
      if (thumbs[current]) thumbs[current].classList.add('product-gallery__thumb--active');
      if (thumbs[current]) thumbs[current].setAttribute('aria-selected', 'true');
    }

    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () { goTo(i); });
    });

    if (prevBtn) prevBtn.addEventListener('click', () => goTo((current - 1 + slides.length) % slides.length));
    if (nextBtn) nextBtn.addEventListener('click', () => goTo((current + 1) % slides.length));

    // Touch swipe on gallery
    let galleryTouchStart = 0;
    const galleryMain = gallery.querySelector('.product-gallery__main');
    if (galleryMain && slides.length > 1) {
      galleryMain.addEventListener('touchstart', e => {
        galleryTouchStart = e.changedTouches[0].clientX;
      }, { passive: true });
      galleryMain.addEventListener('touchend', e => {
        const diff = galleryTouchStart - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 40) {
          goTo(diff > 0
            ? (current + 1) % slides.length
            : (current - 1 + slides.length) % slides.length
          );
        }
      }, { passive: true });
    }

    // Zoom on hover (desktop)
    const zoomContainers = $$('[data-zoom]', gallery);
    zoomContainers.forEach(function (container) {
      container.addEventListener('mousemove', function (e) {
        const rect = container.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width * 100).toFixed(1);
        const y = ((e.clientY - rect.top) / rect.height * 100).toFixed(1);
        container.style.setProperty('--zoom-x', x + '%');
        container.style.setProperty('--zoom-y', y + '%');
      });

      container.addEventListener('click', function () {
        container.classList.toggle('is-zoomed');
      });

      container.addEventListener('mouseleave', function () {
        container.classList.remove('is-zoomed');
      });
    });
  }

  /* ================================================
     7. PRODUCT VARIANT SELECTION
  ================================================ */
  function initProductVariants() {
    const productJsonEl = document.querySelector('[id^="product-json-"]');
    if (!productJsonEl) return;

    let productData;
    try {
      productData = JSON.parse(productJsonEl.textContent);
    } catch (e) {
      console.warn('[Walkeaze] Could not parse product JSON', e);
      return;
    }

    const { variants, options, color_option_index, size_option_index } = productData;

    // Current selected options state
    let selectedOptions = [];
    const form = document.querySelector('.product-form');
    if (!form) return;

    // Initialize from current variant
    const variantInput = form.querySelector('[data-variant-id]');
    const currentVariantId = variantInput ? parseInt(variantInput.value, 10) : null;
    const currentVariant = variants.find(v => v.id === currentVariantId) || variants[0];

    if (currentVariant) {
      selectedOptions = [...currentVariant.options];
    }

    function findVariant(opts) {
      return variants.find(v =>
        v.options.every((opt, i) => opts[i] === undefined || opt === opts[i])
      ) || null;
    }

    function updateUI(variant) {
      if (!variant) return;

      // Update variant input
      if (variantInput) variantInput.value = variant.id;

      // Sync buy-now button variant id
      var buyNowBtn = document.getElementById('buy-now-btn');
      if (buyNowBtn) buyNowBtn.dataset.variantId = variant.id;

      // Update price
      updatePrice(variant);

      // Update button state
      updateCartButton(variant);

      // Update SKU
      const skuEl = document.querySelector('[data-sku]');
      if (skuEl) skuEl.textContent = variant.sku || 'N/A';

      // Update URL
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());

      // Update gallery if variant has image
      if (variant.featured_image) {
        updateGalleryForVariant(variant.featured_image);
      }
    }

    function updatePrice(variant) {
      const priceWrap = document.querySelector('#product-price');
      if (!priceWrap) return;

      const price = variant.price;
      const comparePrice = variant.compare_at_price;
      const formatter = new Intl.NumberFormat('en-PK');

      let html = '<div class="price-wrap';
      if (comparePrice && comparePrice > price) html += ' price-wrap--on-sale';
      html += '">';

      html += '<span class="price-current">Rs. ' + formatter.format(price / 100) + '</span>';

      if (comparePrice && comparePrice > price) {
        const savings = Math.round((comparePrice - price) * 100 / comparePrice);
        html += '<span class="price-compare"><s>Rs. ' + formatter.format(comparePrice / 100) + '</s></span>';
        html += '<span class="price-badge-sale">' + savings + '% OFF</span>';
      }

      if (!variant.available) {
        html += '<span class="price-sold-out">Sold Out</span>';
      }

      html += '</div>';
      priceWrap.innerHTML = html;
    }

    function updateCartButton(variant) {
      const addBtn = document.querySelector('#add-to-cart-btn');
      if (!addBtn) return;
      const textEl = addBtn.querySelector('.add-to-cart-text');

      if (!variant.available) {
        addBtn.disabled = true;
        addBtn.setAttribute('aria-disabled', 'true');
        if (textEl) textEl.textContent = 'Sold Out';
      } else {
        addBtn.disabled = false;
        addBtn.removeAttribute('aria-disabled');
        if (textEl) textEl.textContent = 'Add to Bag';
      }
    }

    function updateGalleryForVariant(imageSrc) {
      const imgs = document.querySelectorAll('.product-gallery__img--main');
      imgs.forEach(function (img) {
        if (img.src.includes(imageSrc.split('?')[0].split('/').pop().split('_')[0])) {
          const thumb = img.closest('.product-gallery__slide');
          if (thumb) {
            const idx = [...document.querySelectorAll('.product-gallery__slide')].indexOf(thumb);
            if (idx > -1) {
              // Trigger thumbnail click
              const thumbBtns = document.querySelectorAll('.product-gallery__thumb');
              if (thumbBtns[idx]) thumbBtns[idx].click();
            }
          }
        }
      });
    }

    function onOptionChange(optionIndex, value) {
      // Clear validation errors on option change
      form.querySelectorAll('.product-option--error').forEach(function (el) {
        el.classList.remove('product-option--error');
      });
      selectedOptions[optionIndex] = value;

      // Update selected display text
      if (color_option_index === optionIndex) {
        const el = document.querySelector('#selected-color');
        if (el) el.textContent = value;
      }
      if (size_option_index === optionIndex) {
        const el = document.querySelector('#selected-size');
        if (el) el.textContent = value;
      }

      // Find matching variant
      const variant = findVariant(selectedOptions);
      if (variant) updateUI(variant);

      // Update button states (which options are available)
      updateOptionAvailability();
    }

    function updateOptionAvailability() {
      // Color swatches
      $$('[data-option-index]').forEach(function (btn) {
        const optIdx = parseInt(btn.dataset.optionIndex, 10);
        const value = btn.dataset.optionValue;

        // Check if any variant with this option + currently selected others is available
        const testOpts = [...selectedOptions];
        testOpts[optIdx] = value;

        const isAvailable = variants.some(function (v) {
          return v.available && v.options.every(function (opt, i) {
            return testOpts[i] === undefined || opt === testOpts[i];
          });
        });

        if (btn.classList.contains('product-color-img-swatch')) {
          btn.classList.toggle('is-unavailable', !isAvailable);
        } else if (btn.classList.contains('size-btn')) {
          btn.classList.toggle('size-btn--sold-out', !isAvailable);
        }
      });
    }

    // Color swatches (image-based)
    $$('.product-color-img-swatch[data-option-value]').forEach(function (swatch) {
      swatch.addEventListener('click', function () {
        if (this.classList.contains('is-unavailable')) return;
        const optIdx = parseInt(this.dataset.optionIndex, 10);
        const value = this.dataset.optionValue;

        $$('.product-color-img-swatch[data-option-index="' + optIdx + '"]').forEach(function (s) {
          s.classList.remove('is-active');
          s.setAttribute('aria-pressed', 'false');
        });
        this.classList.add('is-active');
        this.setAttribute('aria-pressed', 'true');

        onOptionChange(optIdx, value);
      });
    });

    // Size buttons
    $$('.size-btn[data-option-value]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (this.classList.contains('size-btn--sold-out')) return;
        const optIdx = parseInt(this.dataset.optionIndex, 10);
        const value = this.dataset.optionValue;

        // Update active state
        $$('.size-btn[data-option-index="' + optIdx + '"]').forEach(function (b) {
          b.classList.remove('size-btn--selected');
          b.setAttribute('aria-pressed', 'false');
        });
        this.classList.add('size-btn--selected');
        this.setAttribute('aria-pressed', 'true');

        onOptionChange(optIdx, value);
      });
    });

    // Generic selects
    $$('.product-option__select[data-option-index]').forEach(function (select) {
      select.addEventListener('change', function () {
        const optIdx = parseInt(this.dataset.optionIndex, 10);
        onOptionChange(optIdx, this.value);
      });
    });

    // Initialize availability
    updateOptionAvailability();

    // Require explicit selection: clear auto-selected visual state on load
    document.querySelectorAll('.product-color-img-swatch').forEach(function (s) {
      s.classList.remove('is-active');
      s.setAttribute('aria-pressed', 'false');
    });
    document.querySelectorAll('.size-btn').forEach(function (b) {
      b.classList.remove('size-btn--selected');
      b.setAttribute('aria-pressed', 'false');
    });
    var colorDisp = document.querySelector('#selected-color');
    if (colorDisp) colorDisp.textContent = '';
    var sizeDisp = document.querySelector('#selected-size');
    if (sizeDisp) sizeDisp.textContent = '';
  }

  /* ================================================
     8. QUANTITY SELECTOR
  ================================================ */
  function initQuantitySelector() {
    $$('.product-quantity__controls').forEach(function (control) {
      const input = control.querySelector('.product-quantity__input');
      const minusBtn = control.querySelector('[data-qty-minus]');
      const plusBtn = control.querySelector('[data-qty-plus]');
      if (!input) return;

      if (minusBtn) {
        minusBtn.addEventListener('click', function () {
          const val = parseInt(input.value, 10) || 1;
          if (val > 1) input.value = val - 1;
        });
      }

      if (plusBtn) {
        plusBtn.addEventListener('click', function () {
          const val = parseInt(input.value, 10) || 1;
          input.value = val + 1;
        });
      }

      input.addEventListener('change', function () {
        const val = parseInt(this.value, 10);
        if (isNaN(val) || val < 1) this.value = 1;
      });
    });
  }

  /* ================================================
     9. ADD TO CART - AJAX
  ================================================ */
  function initAddToCart() {
    $$('.product-form').forEach(function (form) {
      form.addEventListener('submit', async function (e) {
        e.preventDefault();

        const addBtn = form.querySelector('#add-to-cart-btn');
        if (addBtn && addBtn.disabled) return;

        // Require explicit colour + size selection before adding to bag
        var hasColors = !!form.querySelector('.product-color-img-swatch');
        var hasSizes  = !!form.querySelector('.size-btn');
        var colorPicked = !hasColors || !!form.querySelector('.product-color-img-swatch.is-active');
        var sizePicked  = !hasSizes  || !!form.querySelector('.size-btn.size-btn--selected');
        if (!colorPicked || !sizePicked) {
          if (!colorPicked) {
            var colorBlock = form.querySelector('.product-option--color');
            if (colorBlock) {
              colorBlock.classList.add('product-option--error');
              colorBlock.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }
          if (!sizePicked) {
            var sizeBlock = form.querySelector('.product-option--size');
            if (sizeBlock) {
              sizeBlock.classList.add('product-option--error');
              if (colorPicked) sizeBlock.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }
          setTimeout(function () {
            form.querySelectorAll('.product-option--error').forEach(function (el) {
              el.classList.remove('product-option--error');
            });
          }, 3000);
          return;
        }

        const variantInput = form.querySelector('[data-variant-id]');
        const qtyInput = form.querySelector('.product-quantity__input');
        const variantId = variantInput ? parseInt(variantInput.value, 10) : null;
        const quantity = qtyInput ? parseInt(qtyInput.value, 10) : 1;

        if (!variantId) {
          console.warn('[Walkeaze] No variant ID found');
          return;
        }

        // Loading state
        if (addBtn) addBtn.classList.add('is-loading');

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: quantity })
          });

          if (!res.ok) {
            const err = await res.json();
            throw new Error(err.description || 'Could not add to cart');
          }

          const item = await res.json();

          // Update cart count
          await updateCartCount();

          // Open cart drawer (preferred) or fall back to toast
          if (window.WkzCart) {
            window.WkzCart.open();
          } else {
            showCartNotification(item.title || 'Item added');
          }

        } catch (error) {
          console.error('[Walkeaze] Add to cart error:', error);
          alert(error.message || 'Something went wrong. Please try again.');
        } finally {
          if (addBtn) addBtn.classList.remove('is-loading');
        }
      });
    });

    // Quick add buttons on product cards
    $$('[data-quick-add]').forEach(function (btn) {
      btn.addEventListener('click', async function (e) {
        e.stopPropagation();
        const variantId = parseInt(this.dataset.variantId, 10);
        if (!variantId) return;

        const originalText = this.textContent;
        this.textContent = '...';
        this.disabled = true;

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: 1 })
          });

          if (!res.ok) throw new Error('Could not add to cart');

          await updateCartCount();

          if (window.WkzCart) {
            window.WkzCart.open();
          } else {
            showCartNotification('Added to bag');
          }

          this.textContent = 'Added';
          setTimeout(() => {
            this.textContent = originalText;
            this.disabled = false;
          }, 2000);

        } catch (error) {
          this.textContent = originalText;
          this.disabled = false;
          console.error('[Walkeaze] Quick add error:', error);
        }
      });
    });
  }

  async function updateCartCount() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      const countEl = document.querySelector('#cart-count');
      if (countEl) {
        countEl.textContent = cart.item_count;
        countEl.classList.toggle('cart-count--hidden', cart.item_count === 0);
        // Bounce animation
        countEl.animate([
          { transform: 'scale(1.5)' },
          { transform: 'scale(1)' }
        ], { duration: 300, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)' });
      }
    } catch (e) {
      // Silently fail
    }
  }

  function showCartNotification(title) {
    const notification = document.querySelector('#cart-notification');
    if (!notification) return;

    const textEl = notification.querySelector('.cart-notification__text');
    if (textEl) textEl.textContent = title + ' added to bag';

    notification.classList.add('is-visible');

    // Auto-hide after 3 seconds
    clearTimeout(notification._hideTimer);
    notification._hideTimer = setTimeout(function () {
      notification.classList.remove('is-visible');
    }, 3500);
  }

  /* ================================================
     10. WISHLIST (localStorage)
  ================================================ */
  function initWishlist() {
    const WISHLIST_KEY = 'wkz-wishlist';

    function getWishlist() {
      try {
        return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || [];
      } catch (e) {
        return [];
      }
    }

    function saveWishlist(list) {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
    }

    function isWishlisted(productId) {
      return getWishlist().includes(String(productId));
    }

    function updateWishlistButton(btn, wishlisted) {
      btn.classList.toggle('is-wishlisted', wishlisted);
      btn.setAttribute('aria-label',
        wishlisted ? 'Remove from wishlist' : 'Add to wishlist'
      );
    }

    // Initialize all wishlist buttons
    function syncButtons() {
      $$('[data-wishlist-btn]').forEach(function (btn) {
        const productId = btn.dataset.productId;
        if (productId) {
          updateWishlistButton(btn, isWishlisted(productId));
        }
      });
    }

    $$('[data-wishlist-btn]').forEach(function (btn) {
      const productId = btn.dataset.productId;
      if (!productId) return;

      // Set initial state
      updateWishlistButton(btn, isWishlisted(productId));

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        const list = getWishlist();
        const id = String(productId);
        const idx = list.indexOf(id);
        let wishlisted;

        if (idx > -1) {
          list.splice(idx, 1);
          wishlisted = false;
        } else {
          list.push(id);
          wishlisted = true;
        }

        saveWishlist(list);
        updateWishlistButton(btn, wishlisted);

        // Animate heart
        btn.animate([
          { transform: 'scale(1.3)' },
          { transform: 'scale(1)' }
        ], { duration: 300, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)' });
      });
    });
  }

  /* ================================================
     11. PRODUCT ACCORDIONS
  ================================================ */
  function initAccordions() {
    $$('[data-accordion]').forEach(function (accordion) {
      const trigger = accordion.querySelector('.product-accordion__trigger');
      const body = accordion.querySelector('.product-accordion__body');
      if (!trigger || !body) return;

      trigger.addEventListener('click', function () {
        const expanded = this.getAttribute('aria-expanded') === 'true';
        this.setAttribute('aria-expanded', String(!expanded));
        body.setAttribute('aria-hidden', String(expanded));
      });
    });
  }

  /* ================================================
     12. FILTER GROUP ACCORDIONS
  ================================================ */
  function initFilterAccordions() {
    $$('.filter-group__toggle').forEach(function (toggle) {
      toggle.addEventListener('click', function () {
        const expanded = this.getAttribute('aria-expanded') === 'true';
        this.setAttribute('aria-expanded', String(!expanded));
      });
    });
  }

  /* ================================================
     13. CART NOTIFICATION AUTO-DISMISS
  ================================================ */
  function initCartNotification() {
    const notification = document.querySelector('#cart-notification');
    if (!notification) return;

    // Clicking anywhere on it goes to cart
    notification.addEventListener('click', function (e) {
      if (!e.target.classList.contains('cart-notification__link')) {
        window.location.href = '/cart';
      }
    });
  }

  /* ================================================
     14. SCROLL REVEAL ANIMATIONS
  ================================================ */
  function initScrollReveal() {
    if (!('IntersectionObserver' in window)) return;

    const revealEls = $$('.product-card, .category-grid__grid-item, .footer-column, .related-products__grid .product-card');

    const observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
          entry.target.dataset.wkzRevealed = '1';
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.08,
      rootMargin: '0px 0px -40px 0px'
    });

    revealEls.forEach(function (el, i) {
      // Never re-hide an element that already revealed â€” this is what caused
      // visible cards to jump back down ("slide down first") on re-init.
      if (el.dataset.wkzRevealed) return;
      el.style.opacity = '0';
      el.style.transform = 'translateY(20px)';
      el.style.transition = 'opacity 0.5s ease ' + (i % 4 * 0.08) + 's, transform 0.5s ease ' + (i % 4 * 0.08) + 's';
      observer.observe(el);

      // Safety net: if the observer never fires (already on-screen / layout race),
      // force the slide-up to complete so the animation always works.
      setTimeout(function () {
        if (!el.dataset.wkzRevealed) {
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
          el.dataset.wkzRevealed = '1';
          observer.unobserve(el);
        }
      }, 700 + (i % 4 * 80));
    });
  }

  /* ================================================
     15. IMAGE LAZY LOADING FALLBACK
  ================================================ */
  function initLazyLoad() {
    if ('loading' in HTMLImageElement.prototype) return; // Native lazy loading supported

    const imgs = $$('img[loading="lazy"]');
    if (!('IntersectionObserver' in window)) {
      imgs.forEach(img => { img.src = img.dataset.src || img.src; });
      return;
    }

    const observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          const img = entry.target;
          if (img.dataset.src) img.src = img.dataset.src;
          observer.unobserve(img);
        }
      });
    });

    imgs.forEach(img => observer.observe(img));
  }

  /* ================================================
     16. SORT SELECT URL UPDATE
  ================================================ */
  function initSortSelect() {
    const sortSelect = $('[data-sort-select]');
    if (!sortSelect) return;
    sortSelect.addEventListener('change', function () {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', this.value);
      window.location.href = url.toString();
    });
  }

  /* ================================================
     17. SMOOTH SCROLL FOR ANCHOR LINKS
  ================================================ */
  function initSmoothScroll() {
    document.addEventListener('click', function (e) {
      const link = e.target.closest('a[href^="#"]');
      if (!link) return;
      const target = document.querySelector(link.getAttribute('href'));
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  /* ================================================
     18. HEADER HEIGHT CSS VAR SYNC
  ================================================ */
  function syncHeaderHeight() {
    const header = $('#site-header');
    const annBar = $('#announcement-bar');

    function update() {
      const hh = header ? header.offsetHeight : 72;
      const ah = annBar && annBar.style.display !== 'none' ? annBar.offsetHeight : 0;
      document.documentElement.style.setProperty('--header-height', hh + 'px');
      document.documentElement.style.setProperty('--ann-bar-height', ah + 'px');
    }

    update();
    window.addEventListener('resize', debounce(update, 100));
  }

  /* ================================================
     19. VARIANT IMAGE SWATCH HOVER (product cards)
  ================================================ */
  function initSwatchHover() {
  /* View Toggle */
  (function () {
    var grid = document.getElementById('product-grid');
    if (!grid) return;
    $$('.wkz-coll-view-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var view = this.dataset.view;
        grid.dataset.view = view;
        $$('.wkz-coll-view-btn').forEach(function (b) { b.classList.remove('is-active'); });
        this.classList.add('is-active');
        try { localStorage.setItem('wkz_grid_view', view); } catch(e) {}
      });
    });
    // Restore saved preference (default 4-col if nothing saved or stale "2")
    try {
      var saved = localStorage.getItem('wkz_grid_view');
      if (!saved || saved === '2') saved = '4';
      grid.dataset.view = saved;
      $$('.wkz-coll-view-btn').forEach(function (b) {
        b.classList.toggle('is-active', b.dataset.view === saved);
      });
    } catch(e) {}
  })();

  // Blink "SALE" red in announcement bar
  document.querySelectorAll('.announcement-bar__item').forEach(function(el) {
    el.innerHTML = el.innerHTML.replace(/\bSALE\b/g, '<span class="sale-blink">SALE</span>');
  });

    $$('.product-card').forEach(function (card) {
      const mainImg = card.querySelector('.product-card__img--main');
      if (!mainImg) return;
      const hoverImg = card.querySelector('.product-card__img--hover');

      const originalSrc = mainImg.src;
      const originalSrcset = mainImg.srcset || '';
      const originalHoverSrc = hoverImg ? hoverImg.src : '';
      const originalHoverSrcset = hoverImg ? (hoverImg.srcset || '') : '';

      $$('.product-card__swatch-img-btn', card).forEach(function (btn) {
        btn.addEventListener('mouseenter', function () {
          const newSrc = this.dataset.swatchSrc;
          const newSrcset = this.dataset.swatchSrcset;
          if (newSrc) {
            if (newSrcset) { mainImg.srcset = newSrcset; } else { mainImg.removeAttribute('srcset'); }
            mainImg.src = newSrc;
            if (hoverImg) {
              if (newSrcset) { hoverImg.srcset = newSrcset; } else { hoverImg.removeAttribute('srcset'); }
              hoverImg.src = newSrc;
            }
          }
          $$('.product-card__swatch-img-btn', card).forEach(b => b.classList.remove('is-active'));
          this.classList.add('is-active');
        });

        btn.addEventListener('click', function (e) {
          e.preventDefault();
          const newSrc = this.dataset.swatchSrc;
          const newSrcset = this.dataset.swatchSrcset;
          if (newSrc) {
            if (newSrcset) { mainImg.srcset = newSrcset; } else { mainImg.removeAttribute('srcset'); }
            mainImg.src = newSrc;
            if (hoverImg) {
              if (newSrcset) { hoverImg.srcset = newSrcset; } else { hoverImg.removeAttribute('srcset'); }
              hoverImg.src = newSrc;
            }
          }
          $$('.product-card__swatch-img-btn', card).forEach(b => b.classList.remove('is-active'));
          this.classList.add('is-active');

          // Update all product links in card to point to this variant
          const variantUrl = this.dataset.variantUrl;
          if (variantUrl) {
            $$('a[href]', card).forEach(function (a) {
              if (a.href.includes('/products/')) a.href = variantUrl;
            });
            // Update ATC hidden variant id
            const vid = card.querySelector('.product-card__atc-vid');
            const vId = this.dataset.variantId;
            if (vid && vId) vid.value = vId;
          }

          // Update size availability for this color
          const availStr = this.dataset.availSizes || '';
          const availArr = availStr ? availStr.split(',').map(s => s.trim()) : [];
          $$('.product-card__size', card).forEach(function (sz) {
            const szVal = sz.dataset.size || sz.textContent.trim();
            if (availArr.length > 0) {
              sz.classList.toggle('product-card__size--na', !availArr.includes(szVal));
            }
          });
        });
      });

      // Restore original on card mouseleave if no swatch was clicked (i.e. none active)
      card.addEventListener('mouseleave', function () {
        const anyActive = card.querySelector('.product-card__swatch-img-btn.is-active');
        if (!anyActive) {
          mainImg.src = originalSrc;
          if (originalSrcset) mainImg.srcset = originalSrcset;
          if (hoverImg && originalHoverSrc) {
            hoverImg.src = originalHoverSrc;
            if (originalHoverSrcset) hoverImg.srcset = originalHoverSrcset;
          }
        }
      });
    });
  }

  /* ================================================
     20. RECENTLY VIEWED (localStorage tracker + renderer)
  ================================================ */
  function initRecentlyViewed() {
    const RV_KEY = 'wkz-recently-viewed';
    const MAX_ITEMS = 12;

    function getViewed() {
      try { return JSON.parse(localStorage.getItem(RV_KEY)) || []; } catch (e) { return []; }
    }

    function saveViewed(list) {
      localStorage.setItem(RV_KEY, JSON.stringify(list));
    }

    // Track current product
    const productEl = document.querySelector('[data-product-id]');
    if (productEl && productEl.dataset.productId) {
      const id = productEl.dataset.productId;
      const title = document.querySelector('.product-title') ? document.querySelector('.product-title').textContent.trim() : '';
      const url = window.location.pathname;
      const imgEl = document.querySelector('.product-gallery__img--main, .product-gallery__img');
      const img = imgEl ? imgEl.src : '';
      const priceEl = document.querySelector('.price-current');
      const price = priceEl ? priceEl.textContent.trim() : '';

      if (id && url) {
        const entry = { id, title, url, img, price };
        const list = getViewed().filter(function (item) { return item.id !== id; });
        list.unshift(entry);
        if (list.length > MAX_ITEMS) list.length = MAX_ITEMS;
        saveViewed(list);
      }
    }

    // Render recently viewed section
    const rvSection = document.querySelector('#recently-viewed');
    const rvGrid = document.querySelector('#recently-viewed-grid');
    if (!rvSection || !rvGrid) return;

    const currentId = productEl ? productEl.dataset.productId : null;
    const viewed = getViewed().filter(function (item) { return item.id !== currentId; }).slice(0, 8);

    if (viewed.length === 0) return;

    rvSection.style.display = '';

    viewed.forEach(function (item) {
      const card = document.createElement('div');
      card.className = 'product-card recently-viewed__card';
      card.innerHTML = [
        '<div class="product-card__media">',
        '  <a href="' + item.url + '" class="product-card__media-link" tabindex="-1">',
        item.img
          ? '<img src="' + item.img + '" alt="' + (item.title || '') + '" class="product-card__img product-card__img--main" loading="lazy">'
          : '<div class="product-card__img product-card__img--placeholder" style="background:var(--border);"></div>',
        '  </a>',
        '</div>',
        '<div class="product-card__info">',
        '  <h3 class="product-card__title">',
        '    <a href="' + item.url + '" class="product-card__title-link">' + (item.title || '') + '</a>',
        '  </h3>',
        item.price ? '<div class="product-card__price"><div class="price-wrap"><span class="price-current">' + item.price + '</span></div></div>' : '',
        '</div>'
      ].join('');
      rvGrid.appendChild(card);
    });
  }

  /* ================================================
     21. PRODUCT RECOMMENDATIONS (Shopify API)
  ================================================ */
  function initProductRecommendations() {
    const section = document.querySelector('#product-recommendations');
    if (!section) return;

    const productId = section.dataset.productId;
    if (!productId) return;

    const grid = section.querySelector('.product-recommendations__grid');
    if (!grid) return;

    fetch('/recommendations/products.json?product_id=' + productId + '&limit=8')
      .then(function (res) { return res.json(); })
      .then(function (data) {
        const products = data.products;
        if (!products || products.length === 0) {
          section.style.display = 'none';
          return;
        }

        grid.innerHTML = '';
        products.forEach(function (product) {
          const imgSrc = product.featured_image
            ? product.featured_image.replace(/(\.[a-z]+)(\?|$)/, '_400x$1$2')
            : '';
          const price = product.price_min
            ? 'Rs. ' + new Intl.NumberFormat('en-PK').format(product.price_min / 100)
            : '';
          const comparePrice = product.compare_at_price_min && product.compare_at_price_min > product.price_min
            ? 'Rs. ' + new Intl.NumberFormat('en-PK').format(product.compare_at_price_min / 100)
            : '';

          const card = document.createElement('div');
          card.className = 'product-card';
          card.innerHTML = [
            '<div class="product-card__media">',
            '  <a href="/products/' + product.handle + '" class="product-card__media-link" tabindex="-1">',
            imgSrc
              ? '<img src="' + imgSrc + '" alt="' + product.title + '" class="product-card__img product-card__img--main" loading="lazy">'
              : '<div class="product-card__img product-card__img--placeholder" style="background:var(--border);"></div>',
            '  </a>',
            product.available
              ? '<div class="product-card__quick-add"><a href="/products/' + product.handle + '" class="product-card__quick-add-btn btn btn--primary">Select Options</a></div>'
              : '<div class="product-card__quick-add"><button class="product-card__quick-add-btn btn btn--primary" disabled>Sold Out</button></div>',
            '</div>',
            '<div class="product-card__info">',
            '  <h3 class="product-card__title"><a href="/products/' + product.handle + '" class="product-card__title-link">' + product.title + '</a></h3>',
            '  <div class="product-card__price"><div class="price-wrap' + (comparePrice ? ' price-wrap--on-sale' : '') + '">',
            '    <span class="price-current">' + price + '</span>',
            comparePrice ? '<span class="price-compare"><s>' + comparePrice + '</s></span>' : '',
            '  </div></div>',
            '</div>'
          ].join('');
          grid.appendChild(card);
        });

        // Re-run quick-add listeners and slider state for newly added cards
        initScrollReveal();
        initProductSliders();
      })
      .catch(function () {
        section.style.display = 'none';
      });
  }

  /* ================================================
     22. PRODUCT SLIDERS (recommendations + recently viewed)
  ================================================ */
  function initProductSliders() {
    document.querySelectorAll('.product-slider-wrap').forEach(function (wrap) {
      const slider = wrap.querySelector('.product-slider');
      const prevBtn = wrap.querySelector('.product-slider-btn--prev');
      const nextBtn = wrap.querySelector('.product-slider-btn--next');
      if (!slider) return;

      function getScrollAmount() {
        const card = slider.querySelector('.product-card');
        return card ? card.offsetWidth + 16 : slider.clientWidth * 0.8;
      }

      function updateBtns() {
        const sl = Math.round(slider.scrollLeft);
        const max = slider.scrollWidth - slider.clientWidth;
        if (prevBtn) prevBtn.style.opacity = sl <= 2 ? '0.35' : '1';
        if (nextBtn) nextBtn.style.opacity = sl >= max - 2 ? '0.35' : '1';
      }

      if (prevBtn) prevBtn.addEventListener('click', function () {
        slider.scrollBy({ left: -getScrollAmount(), behavior: 'smooth' });
      });
      if (nextBtn) nextBtn.addEventListener('click', function () {
        slider.scrollBy({ left: getScrollAmount(), behavior: 'smooth' });
      });

      slider.addEventListener('scroll', updateBtns, { passive: true });
      updateBtns();
    });
  }

  /* ================================================
     23. FOOTER NEWSLETTER FORM
  ================================================ */
  function initFooterNewsletter() {
    const form = document.querySelector('#footer-newsletter-form');
    if (!form) return;

    form.addEventListener('submit', function (e) {
      const input = form.querySelector('.footer-newsletter__input');
      const btn = form.querySelector('.footer-newsletter__btn');
      if (!input || !input.value.trim()) { e.preventDefault(); return; }

      // Show brief success state then allow form to submit
      if (btn) {
        btn.innerHTML = '<svg width="18" height="18" viewBox="0 0 20 20" fill="none"><path d="M3 10l4 4 10-8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        setTimeout(function () {
          btn.innerHTML = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M4 10h12M10 4l6 6-6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        }, 3000);
      }
    });
  }

  /* ================================================
     24. PRODUCT CARD - QTY + ADD TO CART
  ================================================ */
  function initCardAddToCart() {
    // Quantity buttons on product cards
    document.addEventListener('click', function (e) {
      const qtyBtn = e.target.closest('[data-card-qty]');
      if (!qtyBtn) return;
      const card = qtyBtn.closest('.product-card');
      if (!card) return;
      const numEl = card.querySelector('.product-card__qty-num');
      if (!numEl) return;
      let current = parseInt(numEl.textContent, 10) || 1;
      const delta = parseInt(qtyBtn.dataset.cardQty, 10);
      current = Math.max(1, current + delta);
      numEl.textContent = current;
    });

    // ATC form submit - AJAX
    document.addEventListener('submit', function (e) {
      const form = e.target.closest('[data-card-atc]');
      if (!form) return;
      e.preventDefault();

      const btn = form.querySelector('.product-card__atc-btn');
      if (!btn || btn.disabled) return;

      const vid = form.querySelector('.product-card__atc-vid');
      const numEl = form.closest('.product-card__info').querySelector('.product-card__qty-num');
      const qty = numEl ? (parseInt(numEl.textContent, 10) || 1) : 1;
      if (!vid || !vid.value) return;

      btn.classList.add('is-adding');
      btn.disabled = true;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: parseInt(vid.value, 10), quantity: qty })
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data.status) {
            // error from Shopify
            btn.classList.remove('is-adding');
            btn.disabled = false;
            alert(data.description || 'Could not add to cart.');
            return;
          }
          btn.classList.remove('is-adding');
          btn.classList.add('is-added');
          btn.innerHTML = '<svg width="13" height="13" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M3 10l4 4 10-8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg> ADDED!';

          // Update cart count in header
          fetch('/cart.js')
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              const countEl = document.getElementById('cart-count');
              if (countEl) {
                countEl.textContent = cart.item_count;
                countEl.classList.toggle('cart-count--hidden', cart.item_count === 0);
              }
            });

          setTimeout(function () {
            btn.classList.remove('is-added');
            btn.disabled = false;
            btn.innerHTML = '<svg width="13" height="13" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M6 7V5.5A4 4 0 0 1 14 5.5V7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M3 7h14l-1.5 10H4.5L3 7z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg> ADD TO CART';
          }, 2500);
        })
        .catch(function () {
          btn.classList.remove('is-adding');
          btn.disabled = false;
        });
    });
  }

  /* ================================================
     INIT ALL
  ================================================ */
  function init() {
    syncHeaderHeight();
    initAnnouncementBar();
    initHeader();
    initHeroCarousels();
    initCategoryGrid();
    initFilterSidebar();
    initFilterAccordions();
    initProductGallery();
    initProductVariants();
    initQuantitySelector();
    initAddToCart();
    initWishlist();
    initAccordions();
    initCartNotification();
    initScrollReveal();
    initLazyLoad();
    initSortSelect();
    initSmoothScroll();
    initSwatchHover();
    initRecentlyViewed();
    initProductRecommendations();
    initProductSliders();
    initFooterNewsletter();
    initCardAddToCart();
    initBuyNow();
  }

  /* ================================================
     25. BUY NOW - add to cart then go to checkout
  ================================================ */
  function initBuyNow() {
    var btn = document.getElementById('buy-now-btn');
    if (!btn) return;

    // Keep variant id in sync when swatch / size changes
    var atcBtn = document.getElementById('add-to-cart-btn');
    if (atcBtn) {
      var observer = new MutationObserver(function() {
        var hiddenId = document.querySelector('input[name="id"]');
        if (hiddenId) btn.dataset.variantId = hiddenId.value;
      });
      var form = atcBtn.closest('form');
      if (form) observer.observe(form, { subtree: true, attributes: true, attributeFilter: ['value'] });
    }

    btn.addEventListener('click', function() {
      var variantId = this.dataset.variantId;
      var qtyInput = document.querySelector('.product-quantity__input');
      var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
      if (!variantId) return;

      btn.classList.add('is-loading');
      btn.textContent = '...';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: parseInt(variantId, 10), quantity: qty })
      })
      .then(function(r) { return r.json(); })
      .then(function() {
        window.location.href = '/checkout';
      })
      .catch(function() {
        btn.classList.remove('is-loading');
        btn.textContent = btn.dataset.originalText || 'BUY NOW';
      });
    });

    btn.dataset.originalText = btn.textContent.trim();
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();